// ────────────────────────────────────────────────────────────────────
// DIVERSIONS TAB
//
// Dispatcher-side tab where 6 hospital slots can each be marked on
// diversion for one or more capabilities until a specific clock time.
// When a unit goes HA/HB and the dispatcher picks a hospital that's on
// active diversion for the chosen category, the system auto-reroutes to
// the next-closest hospital that IS still accepting that category.
//
// Per operator spec the panel has 6 hospital pickers. Each picker:
//   - Hospital dropdown (sourced from /api/hospitals — the same Excel
//     uploaded via Admin → Hospitals)
//   - Once a hospital is chosen, the hospital's existing capabilities
//     render with a checkbox + an editable HHMM time field
//   - OK button posts each ticked capability as its own diversion entry
//     keyed to that hospital + category + the specified time
//   - Cancel button reverts the row to a clean state
// ────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "./utils";

const SLOT_COUNT = 6;

function _isoFromHHMM(hhmm) {
  // Convert "HHMM" / "HH:MM" → ISO timestamp for today at that wall-clock
  // time (local zone). If the time has already passed today, push to
  // tomorrow so the diversion is treated as still-active.
  const digits = (hhmm || "").replace(/[^0-9]/g, "").padStart(4, "0").slice(0, 4);
  const hh = Math.min(23, parseInt(digits.slice(0, 2), 10) || 0);
  const mm = Math.min(59, parseInt(digits.slice(2, 4), 10) || 0);
  const target = new Date();
  target.setHours(hh, mm, 0, 0);
  if (target.getTime() < Date.now()) {
    target.setDate(target.getDate() + 1);
  }
  return target.toISOString();
}

function _hhmmFromIso(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
}

export function DiversionsTab() {
  const [hospitals, setHospitals] = useState([]);
  const [activeDiversions, setActiveDiversions] = useState([]);
  // 6 slot states — each is { hospitalId, capabilities: { capName: { checked, time } } }
  const [slots, setSlots] = useState(() => Array.from({ length: SLOT_COUNT }, () => ({ hospitalId: "", caps: {} })));

  const refreshDiversions = () => {
    axios.get(`${API}/diversions`, { params: { active_only: true } })
      .then((r) => setActiveDiversions(r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/hospitals`).then((r) => setHospitals(r.data || [])).catch(() => {});
    refreshDiversions();
    const t = setInterval(refreshDiversions, 30000);
    return () => clearInterval(t);
  }, []);

  // Hydrate a slot's checkbox/time state from existing active diversions
  // when the dispatcher picks a hospital — so they can see and modify
  // what's already on the books rather than re-entering blindly.
  const onPickHospital = (slotIdx, hospitalId) => {
    setSlots((prev) => {
      const copy = [...prev];
      const hospital = hospitals.find((h) => h.id === hospitalId);
      const caps = {};
      (hospital?.capabilities || []).forEach((c) => {
        const existing = activeDiversions.find((d) => d.hospital_id === hospitalId && (d.category || "").toUpperCase() === c.toUpperCase());
        caps[c] = {
          checked: !!existing,
          time: existing ? _hhmmFromIso(existing.until) : "",
          existing_id: existing ? existing.id : null,
        };
      });
      copy[slotIdx] = { hospitalId, caps };
      return copy;
    });
  };

  const toggleCap = (slotIdx, capName, field, value) => {
    setSlots((prev) => {
      const copy = [...prev];
      const slot = { ...copy[slotIdx] };
      const caps = { ...slot.caps };
      caps[capName] = { ...(caps[capName] || {}), [field]: value };
      // If the user is editing the time we treat that as an implicit check.
      if (field === "time" && value) caps[capName].checked = true;
      slot.caps = caps;
      copy[slotIdx] = slot;
      return copy;
    });
  };

  const handleOk = async (slotIdx) => {
    const slot = slots[slotIdx];
    if (!slot.hospitalId) return;
    const tasks = [];
    for (const [capName, st] of Object.entries(slot.caps)) {
      if (st.checked && st.time) {
        // If a row was already on the books and the dispatcher unchecked
        // it, drop the existing entry. Otherwise post (or replace) it.
        if (st.existing_id) {
          tasks.push(axios.delete(`${API}/diversions/${st.existing_id}`));
        }
        tasks.push(axios.post(`${API}/diversions`, {
          hospital_id: slot.hospitalId,
          category: capName,
          until: _isoFromHHMM(st.time),
          note: "",
        }));
      } else if (st.existing_id) {
        // Was on diversion, now unchecked — clear it.
        tasks.push(axios.delete(`${API}/diversions/${st.existing_id}`));
      }
    }
    try {
      await Promise.all(tasks);
      refreshDiversions();
      // Keep the slot bound but reset checkbox dirty markers.
      onPickHospital(slotIdx, slot.hospitalId);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Failed to save diversions: " + (e?.message || "network error"));
    }
  };

  const handleCancel = (slotIdx) => {
    setSlots((prev) => {
      const copy = [...prev];
      copy[slotIdx] = { hospitalId: "", caps: {} };
      return copy;
    });
  };

  const removeDiversion = async (id) => {
    try {
      await axios.delete(`${API}/diversions/${id}`);
      refreshDiversions();
    } catch {}
  };

  return (
    <div data-testid="diversions-tab" style={{ background: "#1a1a1a", color: "#FFF", fontFamily: '"Courier New", monospace', padding: 16, height: "100%", overflow: "auto" }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#FFC107", marginBottom: 12, letterSpacing: 1 }}>
        HOSPITAL DIVERSIONS
      </div>
      <div style={{ fontSize: 12, color: "#AAA", marginBottom: 18 }}>
        Mark hospital capabilities as on diversion until a specific clock time. Units sent
        HA/HB will automatically be re-routed to the next-closest hospital still accepting
        the chosen category.
      </div>

      {/* Active diversions banner */}
      <div style={{ background: "#2a1a1a", border: "1px solid #553333", padding: 10, marginBottom: 18, borderRadius: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#FF6644", marginBottom: 6 }}>
          ACTIVE DIVERSIONS ({activeDiversions.length})
        </div>
        {activeDiversions.length === 0 && (
          <div data-testid="no-active-diversions" style={{ fontSize: 11, color: "#888" }}>None — all hospitals accepting all categories.</div>
        )}
        {activeDiversions.map((d) => (
          <div key={d.id} data-testid={`active-diversion-row-${d.id}`} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12, padding: "3px 0", borderBottom: "1px solid #2a1a1a" }}>
            <span style={{ minWidth: 70, color: "#FFA000", fontWeight: 700 }}>{d.hospital_code || "—"}</span>
            <span style={{ flex: 1 }}>{d.hospital_name}</span>
            <span style={{ minWidth: 140, color: "#DDDDFF" }}>{d.category}</span>
            <span style={{ minWidth: 80, color: "#AADDAA" }}>until {_hhmmFromIso(d.until)}</span>
            <button data-testid={`cancel-diversion-${d.id}`} onClick={() => removeDiversion(d.id)} style={{ background: "#552222", color: "#FFF", border: "1px solid #884444", borderRadius: 2, padding: "2px 8px", cursor: "pointer", fontSize: 10 }}>CLEAR</button>
          </div>
        ))}
      </div>

      {/* 6 hospital slots */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
        {slots.map((slot, i) => {
          const hospital = hospitals.find((h) => h.id === slot.hospitalId);
          return (
            <div key={i} data-testid={`diversion-slot-${i + 1}`} style={{ background: "#222", border: "1px solid #444", padding: 10, borderRadius: 4 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>SLOT {i + 1}</div>
              <select data-testid={`diversion-slot-${i + 1}-hospital`} value={slot.hospitalId}
                onChange={(e) => onPickHospital(i, e.target.value)}
                style={{ width: "100%", background: "#111", color: "#FFF", border: "1px solid #555", padding: "4px 6px", fontSize: 12, marginBottom: 8 }}>
                <option value="">-- pick a hospital --</option>
                {hospitals.map((h) => <option key={h.id} value={h.id}>{h.hospital_code} — {h.name}</option>)}
              </select>
              {hospital && (
                <>
                  <div style={{ fontSize: 11, color: "#AAA", marginBottom: 4 }}>Capabilities (check & enter HHMM):</div>
                  <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #333", padding: 6, marginBottom: 8 }}>
                    {(hospital.capabilities || []).map((c) => {
                      const st = slot.caps[c] || { checked: false, time: "" };
                      return (
                        <div key={c} style={{ display: "flex", gap: 6, alignItems: "center", padding: "2px 0", fontSize: 12 }}>
                          <input data-testid={`diversion-slot-${i + 1}-cap-${c}-check`} type="checkbox" checked={!!st.checked}
                            onChange={(e) => toggleCap(i, c, "checked", e.target.checked)} />
                          <span style={{ flex: 1 }}>{c}</span>
                          <input data-testid={`diversion-slot-${i + 1}-cap-${c}-time`} type="text" maxLength={5}
                            value={st.time || ""}
                            onChange={(e) => toggleCap(i, c, "time", e.target.value)}
                            placeholder="HHMM"
                            style={{ width: 60, background: "#111", color: "#FFF", border: "1px solid #555", padding: "2px 4px", fontSize: 12, fontFamily: '"Courier New", monospace' }} />
                        </div>
                      );
                    })}
                    {(hospital.capabilities || []).length === 0 && (
                      <div style={{ color: "#888", fontSize: 11 }}>No capabilities listed for this hospital.</div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button data-testid={`diversion-slot-${i + 1}-cancel`} onClick={() => handleCancel(i)}
                      style={{ background: "#444", color: "#FFF", border: "1px solid #666", padding: "4px 12px", fontSize: 11, cursor: "pointer", borderRadius: 2 }}>CANCEL</button>
                    <button data-testid={`diversion-slot-${i + 1}-ok`} onClick={() => handleOk(i)}
                      style={{ background: "#1E5DA8", color: "#FFF", border: "1px solid #0E3A6F", padding: "4px 12px", fontSize: 11, cursor: "pointer", borderRadius: 2, fontWeight: 700 }}>OK</button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
